import axiosInstance from '../../services/axiosInstance';
import { QrOptions, QrGenerateResult, QrListItem } from './types';

export async function getQrPreviewSvg(content: string, options: Partial<QrOptions> = {}): Promise<string> {
  const { data } = await axiosInstance.post('/api/qrcode/preview', { content, ...options });
  return data.svg;
}

export async function generateQr(content: string, name: string, options: Partial<QrOptions> = {}, categoryIds?: string[]): Promise<QrGenerateResult> {
  const { data } = await axiosInstance.post('/api/qrcode/generate', { content, name, ...options, categoryIds });
  return data;
}

export async function downloadQrTiff(assetId: string): Promise<void> {
  const response = await axiosInstance.get(`/api/qrcode/download/${assetId}`, { responseType: 'blob' });
  const blob = new Blob([response.data], { type: 'image/tiff' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const cd = response.headers['content-disposition'];
  a.download = cd?.match(/filename="(.+)"/)?.[1] || 'qr-code.tif';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function downloadQrPng(assetId: string): Promise<void> {
  const response = await axiosInstance.get(`/api/qrcode/download-png/${assetId}`, { responseType: 'blob' });
  const blob = new Blob([response.data], { type: 'image/png' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const cd = response.headers['content-disposition'];
  a.download = cd?.match(/filename="(.+)"/)?.[1] || 'qr-code.png';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function listQrCodes(): Promise<QrListItem[]> {
  const { data } = await axiosInstance.get('/api/qrcode/list');
  return data.assets;
}

export async function deleteQrCode(assetId: string): Promise<void> {
  await axiosInstance.delete(`/api/assets/${assetId}`);
}

// --- Logo API ---

export interface QrLogo {
  filename: string;
  path: string;
  size: number;
}

export async function uploadQrLogo(file: File): Promise<{ filename: string; originalName: string }> {
  const formData = new FormData();
  formData.append('logo', file);
  const { data } = await axiosInstance.post('/api/qrcode/logo/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function listQrLogos(): Promise<QrLogo[]> {
  const { data } = await axiosInstance.get('/api/qrcode/logo/list');
  return data.logos;
}

export async function deleteQrLogo(filename: string): Promise<void> {
  await axiosInstance.delete(`/api/qrcode/logo/${encodeURIComponent(filename)}`);
}

export interface LogoSearchResult {
  _id: string;
  title: string;
  fileName: string;
  tags: string[];
  previewPaths?: { thumbnail?: string; medium?: string };
  fileType: string;
}

export async function searchAssetsForLogo(query: string): Promise<LogoSearchResult[]> {
  const { data } = await axiosInstance.get(`/api/qrcode/logo/search?q=${encodeURIComponent(query)}`);
  return data.assets;
}

// --- Dynamic QR API ---

export interface DynamicQrItem {
  _id: string;
  qrId: string;
  name: string;
  destinationUrl: string;
  assetId?: string;
  scanCount: number;
  lastScannedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export async function listDynamicQr(): Promise<DynamicQrItem[]> {
  const { data } = await axiosInstance.get('/api/qrcode/dynamic');
  return data.items;
}

export async function createDynamicQr(
  name: string, destinationUrl: string, options: Partial<QrOptions> = {}, baseUrl?: string, categoryIds?: string[],
): Promise<{ redirect: any; asset: any; downloadUrl: string }> {
  const resolvedBase = baseUrl?.trim() || window.location.origin;
  const { data } = await axiosInstance.post('/api/qrcode/dynamic', { name, destinationUrl, baseUrl: resolvedBase, ...options, categoryIds });
  return data;
}

export async function updateDynamicQr(qrId: string, updates: { destinationUrl?: string; name?: string }): Promise<void> {
  await axiosInstance.put(`/api/qrcode/dynamic/${qrId}`, updates);
}

export async function deleteDynamicQr(qrId: string): Promise<void> {
  await axiosInstance.delete(`/api/qrcode/dynamic/${qrId}`);
}
